import { useEffect, useMemo, useState } from 'react';
import axios from 'common/requester';
import { API } from 'common/constants/api-urls';
import { isEmpty, keyBy, omitBy } from 'lodash';
import useFiltersPayloadIssuesKeys from './useFiltersPayloadIssuesKeys';

const useLinbPdtEntities = ({
  selectedPdtEntities,
  pdtEntities,
  isTreeHierarchyEnabled
}) => {
  const [pdtFilters, setPdtFilters] = useState({});
  const issuesKeys = useFiltersPayloadIssuesKeys();

  const entitiesByIds = useMemo(() => keyBy(pdtEntities, 'id'), [pdtEntities]);

  const initPdtProjectsEntitiesFilters = async () => {
    const pdtPayload = {};

    await Promise.all(
      selectedPdtEntities.map(async pdtProject => {
        const entity = entitiesByIds[pdtProject.id] || {};
        const entityFilters = entity?.filters || {};

        const {
          boards = [],
          epics = [],
          labels = [],
          fields = [],
          fix_versions = [],
          parent_issues = []
        } = entityFilters;

        const fieldsObj = fields.reduce((acc, field) => {
          const id = field?.provider_id;
          const values = field?.values || [];
          const isValidField = !!id && values?.length > 0;
          return {
            ...acc,
            ...(isValidField && { [id]: values })
          };
        }, {});

        const boardsIds = boards
          .map(board => board?.provider_id)
          .filter(Boolean);

        const epicsIds = (
          (await Promise.all(
            epics.map(async epicKey => {
              const res = await axios.get(
                `${API.PM_DATA}/epics?keyword=${epicKey}`,
                { params: { exact_match: true } }
              );
              const exactEpicEntity = (res?.items || []).find(
                epicEntity => epicEntity?.value === epicKey
              );
              return exactEpicEntity?.entity_id;
            })
          )) || []
        ).filter(Boolean);

        const oldIssues = omitBy(
          {
            [issuesKeys.parentOne]: epicsIds
          },
          isEmpty
        );

        const parentIssuesIds = parent_issues.map(
          parentIssue => parentIssue.provider_id
        );
        const issuesIds = new Set(
          [...epicsIds, ...parentIssuesIds].filter(Boolean)
        );

        const currPdtPayload = omitBy(
          {
            boards_ids: boardsIds,
            ...(isTreeHierarchyEnabled
              ? { issue_ids: issuesIds }
              : { issues: oldIssues }),
            labels,
            fields: fieldsObj,
            fix_versions
          },
          isEmpty
        );
        if (isEmpty(currPdtPayload)) return;

        pdtPayload[pdtProject.id] = currPdtPayload;
      })
    );

    setPdtFilters(pdtPayload);
  };

  useEffect(() => {
    if (pdtEntities.length > 0) initPdtProjectsEntitiesFilters();
  }, [JSON.stringify(selectedPdtEntities), pdtEntities.length]);

  return pdtFilters;
};

export default useLinbPdtEntities;
